import { useMemo } from 'react';
import '../styles/galaxy.css';

// Astronomy-themed celebration for the first time a user marks an artist
// as "discovered". This fires once per account and is intentionally more
// elaborate than the standard exploration-count milestones because
// discovery is the whole point of Stellar.

const PARTICLE_COUNT = 44;
const PALETTE = ['#fef3c7', '#fde68a', '#a78bfa', '#7dd3fc', '#fbcfe8', '#ffffff'];

function buildParticles() {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => {
    const angle = (i / PARTICLE_COUNT) * 360 + (Math.random() - 0.5) * 6;
    const distance = 170 + Math.random() * 110;
    const delay = Math.random() * 0.25;
    const duration = 1.05 + Math.random() * 0.7;
    const size = 3 + Math.random() * 5;
    const color = PALETTE[i % PALETTE.length];
    const variant = i % 3 === 0 ? 'comet' : 'star';
    return { i, angle, distance, delay, duration, size, color, variant };
  });
}

function FirstDiscoveryModal({ onClose }) {
  const particles = useMemo(buildParticles, []);

  return (
    <div className="galaxy-info-overlay" onClick={onClose}>
      <div className="firstdiscovery-burst" aria-hidden="true">
        {particles.map((p) => (
          <span
            key={p.i}
            className={`firstdiscovery-particle firstdiscovery-particle-${p.variant}`}
            style={{
              '--angle': `${p.angle}deg`,
              '--distance': `${p.distance}px`,
              '--delay': `${p.delay}s`,
              '--duration': `${p.duration}s`,
              '--size': `${p.size}px`,
              '--color': p.color,
            }}
          />
        ))}
      </div>

      <div
        className="galaxy-info-modal milestone-modal firstdiscovery-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="firstdiscovery-title"
      >
        <button className="galaxy-info-close" onClick={onClose} aria-label="Dismiss">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="firstdiscovery-icon" aria-hidden="true">
          <svg viewBox="0 0 64 64" width="72" height="72">
            <defs>
              <radialGradient id="fd-core" cx="50%" cy="40%" r="55%">
                <stop offset="0%" stopColor="#fffbe6" />
                <stop offset="40%" stopColor="#fde68a" />
                <stop offset="100%" stopColor="#a78bfa" />
              </radialGradient>
            </defs>
            <circle cx="32" cy="32" r="14" fill="url(#fd-core)" />
            <g stroke="#fef3c7" strokeWidth="1.6" strokeLinecap="round">
              <line x1="32" y1="4" x2="32" y2="14" />
              <line x1="32" y1="50" x2="32" y2="60" />
              <line x1="4" y1="32" x2="14" y2="32" />
              <line x1="50" y1="32" x2="60" y2="32" />
              <line x1="12" y1="12" x2="19" y2="19" />
              <line x1="45" y1="45" x2="52" y2="52" />
              <line x1="52" y1="12" x2="45" y2="19" />
              <line x1="19" y1="45" x2="12" y2="52" />
            </g>
          </svg>
        </div>

        <h3 id="firstdiscovery-title" className="galaxy-info-title milestone-title">
          A new star, discovered.
        </h3>
        <p className="galaxy-info-text milestone-body">
          You just marked your first artist as discovered. This is what Stellar
          is built for. Every discovery widens your universe and changes the
          shape of the connections you can find next.
        </p>

        <button className="milestone-cta" onClick={onClose}>
          Keep discovering
        </button>
      </div>
    </div>
  );
}

export default FirstDiscoveryModal;
